import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { ApiError } from '../../api/client';
import { Database, PenLine, ChevronLeft, ChevronRight, TrendingDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getCurrencySymbol } from '@/utils/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
} from 'recharts';
import { useFormatting } from '../../hooks/useFormatting';
import { getContributingSourcesForWindow } from '../../hooks/useDrillDown';
import { FORECAST_90_DAY_ENDPOINT } from '../../data/demo-data';
import { ANNUAL_TURNOVER } from '../../data/financial-systems';
import { useTerminology } from '@/hooks/useTerminology';
import { TrendingUp, Target, Info, Lightbulb, Calendar, AlertTriangle, RefreshCw } from 'lucide-react';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { ScenarioOverlayPicker, BASE_ID, SCENARIO_COLOURS, scenarioLabel, ClusterPickerPopover } from './ScenarioOverlayPicker';
import type { ClusterCandidate } from './ScenarioOverlayPicker';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { useBaseForecast } from '../../hooks/useBaseForecast';
import { useForecast } from '../../hooks/useForecast';
import { useScenarioForecasts } from '../../hooks/useScenarioForecasts';
import { useApp } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { applyScenarioToForecast } from '../../lib/scenarioEngine';
import type { ForecastDataPointResponseApi } from '../../api/models/forecast';
import type { ProjectedForecastPointResponseApi } from '../../api/models/scenario';



const CHART_LINE_COLOR = 'var(--accent-sky-500)';
const CHART_THRESHOLD_COLOR = 'var(--mc-error-icon)';

type ViewType = 'quarterly' | 'annual';
type ScenarioType = 'base' | 'conservative' | 'optimistic';

// Generate 3 years of forecast data (quarterly)
const generate3YearData = (scenario: ScenarioType) => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const quarters = [];
  for (let i = 0; i < 12; i++) {
    const quarterMonth = (currentMonth + i * 3) % 12;
    const quarterYear = currentYear + Math.floor((currentMonth + i * 3) / 12);
    const quarterNum = Math.floor(quarterMonth / 3) + 1;
    quarters.push(`Q${quarterNum} ${quarterYear}`);
  }

  const baseValues = {
    base: [
      247000, FORECAST_90_DAY_ENDPOINT, 135000, 98000,
      172000, 155000, 195000, 178000,
      225000, 208000, 252000, 235000,
    ],
    conservative: [
      247000, 55000, 105000, 78000,
      135000, 118000, 158000, 142000,
      185000, 168000, 205000, 188000,
    ],
    optimistic: [
      247000, 95000, 180000, 135000,
      225000, 208000, 255000, 238000,
      295000, 278000, 335000, 318000,
    ],
  };

  // Quarterly cash receipts — BuildRight collects ~83% of quarterly revenue as cash per quarter
  // (45-day debtor days means ~15% of each quarter's billing lands in the next quarter)
  // Base quarterly receipt: ANNUAL_TURNOVER (£5M) ÷ 4 × 0.83 = ~£1,037,500
  // Q1 of Year 1 is suppressed due to known cash flow pressure (aligned with 90-day forecast endpoint)
  const qBase = Math.round((ANNUAL_TURNOVER / 4) * 0.83); // ~£1,037,500
  const revenue = {
    base:         [Math.round(qBase * 0.50), Math.round(qBase * 0.47), Math.round(qBase * 0.55), Math.round(qBase * 0.57),
                   Math.round(qBase * 0.59), Math.round(qBase * 0.62), Math.round(qBase * 0.64), Math.round(qBase * 0.67),
                   Math.round(qBase * 0.69), Math.round(qBase * 0.71), Math.round(qBase * 0.74), Math.round(qBase * 0.76)],
    conservative: [Math.round(qBase * 0.46), Math.round(qBase * 0.43), Math.round(qBase * 0.50), Math.round(qBase * 0.52),
                   Math.round(qBase * 0.54), Math.round(qBase * 0.55), Math.round(qBase * 0.57), Math.round(qBase * 0.59),
                   Math.round(qBase * 0.61), Math.round(qBase * 0.63), Math.round(qBase * 0.65), Math.round(qBase * 0.67)],
    optimistic:   [Math.round(qBase * 0.55), Math.round(qBase * 0.52), Math.round(qBase * 0.60), Math.round(qBase * 0.63),
                   Math.round(qBase * 0.66), Math.round(qBase * 0.69), Math.round(qBase * 0.72), Math.round(qBase * 0.75),
                   Math.round(qBase * 0.78), Math.round(qBase * 0.81), Math.round(qBase * 0.83), Math.round(qBase * 0.86)],
  };

  // Quarterly operating expenses — base monthly expenses ~£63,352 × 3 months = ~£190,056/quarter
  // Q1 Year 1 elevated due to deferred supplier payments from prior period
  const qExp = Math.round(63352 * 3); // ~£190,056
  const expenses = {
    base:         [Math.round(qExp * 1.22), Math.round(qExp * 0.95), Math.round(qExp * 0.96), Math.round(qExp * 0.97),
                   Math.round(qExp * 0.98), Math.round(qExp * 0.99), Math.round(qExp * 1.00), Math.round(qExp * 1.01),
                   Math.round(qExp * 1.02), Math.round(qExp * 1.02), Math.round(qExp * 1.03), Math.round(qExp * 1.04)],
    conservative: [Math.round(qExp * 1.18), Math.round(qExp * 0.96), Math.round(qExp * 0.97), Math.round(qExp * 0.98),
                   Math.round(qExp * 0.99), Math.round(qExp * 1.00), Math.round(qExp * 1.01), Math.round(qExp * 1.02),
                   Math.round(qExp * 1.03), Math.round(qExp * 1.04), Math.round(qExp * 1.04), Math.round(qExp * 1.05)],
    optimistic:   [Math.round(qExp * 1.26), Math.round(qExp * 0.94), Math.round(qExp * 0.95), Math.round(qExp * 0.96),
                   Math.round(qExp * 0.97), Math.round(qExp * 0.98), Math.round(qExp * 0.99), Math.round(qExp * 1.00),
                   Math.round(qExp * 1.01), Math.round(qExp * 1.01), Math.round(qExp * 1.02), Math.round(qExp * 1.03)],
  };

  return quarters.map((quarter, idx) => ({
    quarter,
    year: Math.floor(idx / 4) + 1,
    balance: baseValues[scenario][idx],
    revenue: revenue[scenario][idx],
    expenses: expenses[scenario][idx],
    netCashFlow: revenue[scenario][idx] - expenses[scenario][idx],
  }));
};

// Generate annual summary
const generateAnnualData = (scenario: ScenarioType) => {
  const currentYear = new Date().getFullYear();
  const years = [currentYear.toString(), (currentYear + 1).toString(), (currentYear + 2).toString()];

  const baseValues = {
    base: [172000, 225000, 235000],
    conservative: [135000, 185000, 188000],
    optimistic: [225000, 295000, 318000],
  };

  // Annual cash receipts — sum of quarterly receipts from generate3YearData
  // Year 1 Q1 suppressed → full year ~83% of ANNUAL_TURNOVER × growth trajectory
  // Year 1: ~£3.44M collected (68.7% of £5M — Q1 cash pressure, gradual recovery)
  // Year 2: ~£4.20M collected (84% of £5M — normalised + modest growth)
  // Year 3: ~£4.82M collected (96.4% of £5M — strong collection, volume growth)
  const revenue = {
    base:         [Math.round(ANNUAL_TURNOVER * 0.687), Math.round(ANNUAL_TURNOVER * 0.840), Math.round(ANNUAL_TURNOVER * 0.964)],
    conservative: [Math.round(ANNUAL_TURNOVER * 0.630), Math.round(ANNUAL_TURNOVER * 0.762), Math.round(ANNUAL_TURNOVER * 0.876)],
    optimistic:   [Math.round(ANNUAL_TURNOVER * 0.748), Math.round(ANNUAL_TURNOVER * 0.930), Math.round(ANNUAL_TURNOVER * 1.070)],
  };

  // Annual operating expenses — monthly base £63,352 × 12 = £760,224/year with modest inflation
  const annExp = Math.round(63352 * 12); // £760,224
  const expenses = {
    base:         [Math.round(annExp * 1.29), Math.round(annExp * 1.00), Math.round(annExp * 1.04)],
    conservative: [Math.round(annExp * 1.29), Math.round(annExp * 1.01), Math.round(annExp * 1.05)],
    optimistic:   [Math.round(annExp * 1.29), Math.round(annExp * 0.99), Math.round(annExp * 1.03)],
  };

  return years.map((year, idx) => ({
    year,
    balance: baseValues[scenario][idx],
    revenue: revenue[scenario][idx],
    expenses: expenses[scenario][idx],
    netCashFlow: revenue[scenario][idx] - expenses[scenario][idx],
    growth: idx === 0 ? 0 : ((baseValues[scenario][idx] - baseValues[scenario][idx - 1]) / baseValues[scenario][idx - 1] * 100),
  }));
};

type DrillDownRow =
  | { kind: 'quarterly'; quarter: string; year: number; balance: number; revenue: number; expenses: number; netCashFlow: number; scenarioName?: string }
  | { kind: 'annual'; year: string; balance: number; revenue: number; expenses: number; netCashFlow: number; growth: number; scenarioName?: string };

// Parse a quarter label like "Q2 2026" into a [start, end) window
const getQuarterWindow = (quarterLabel: string): [Date, Date] => {
  const match = quarterLabel.match(/Q(\d)\s+(\d{4})/);
  if (!match) return [new Date(), new Date()];
  const q = parseInt(match[1]) - 1; // 0-indexed quarter
  const year = parseInt(match[2]);
  const start = new Date(year, q * 3, 1);
  const end = new Date(year, q * 3 + 3, 1);
  return [start, end];
};

// Parse an annual label like "2026" into a [start, end) window
const getAnnualWindow = (yearLabel: string): [Date, Date] => {
  const year = parseInt(yearLabel);
  return [new Date(year, 0, 1), new Date(year + 1, 0, 1)];
};

function aggregateScenarioTimelineToQuartersFull(
  points: ProjectedForecastPointResponseApi[],
): { balance: number; revenue: number; expenses: number; netCashFlow: number }[] {
  const buckets = new Map<
    string,
    { balance: number; revenue: number; expenses: number }
  >();

  for (const dp of points) {
    const d = new Date(dp.date);
    const qNum = Math.floor(d.getMonth() / 3) + 1;
    const yearNum = d.getFullYear();
    const key = `Q${qNum} ${yearNum}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.revenue += dp.scenarioIncoming;
      existing.expenses += dp.scenarioOutgoing;
      existing.balance = dp.scenarioBalance;
    } else {
      buckets.set(key, {
        revenue: dp.scenarioIncoming,
        expenses: dp.scenarioOutgoing,
        balance: dp.scenarioBalance,
      });
    }
  }

  return Array.from(buckets.values()).map(data => ({
    balance: data.balance,
    revenue: data.revenue,
    expenses: data.expenses,
    netCashFlow: data.revenue - data.expenses,
  }));
}

function aggregateScenarioTimelineToAnnualFull(
  points: ProjectedForecastPointResponseApi[],
): { balance: number; revenue: number; expenses: number; netCashFlow: number }[] {
  const buckets = new Map<
    number,
    { balance: number; revenue: number; expenses: number }
  >();

  for (const dp of points) {
    const d = new Date(dp.date);
    const yearNum = d.getFullYear();
    const existing = buckets.get(yearNum);
    if (existing) {
      existing.revenue += dp.scenarioIncoming;
      existing.expenses += dp.scenarioOutgoing;
      existing.balance = dp.scenarioBalance;
    } else {
      buckets.set(yearNum, {
        revenue: dp.scenarioIncoming,
        expenses: dp.scenarioOutgoing,
        balance: dp.scenarioBalance,
      });
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([, data]) => ({
      balance: data.balance,
      revenue: data.revenue,
      expenses: data.expenses,
      netCashFlow: data.revenue - data.expenses,
    }));
}

function aggregateTimelineToQuarters(
  timeline: ForecastDataPointResponseApi[],
): { quarter: string; year: number; balance: number; revenue: number; expenses: number; netCashFlow: number }[] {
  const buckets = new Map<
    string,
    { year: number; balance: number; revenue: number; expenses: number }
  >();

  for (const dp of timeline) {
    const d = new Date(dp.date);
    const qNum = Math.floor(d.getMonth() / 3) + 1;
    const yearNum = d.getFullYear();
    const key = `Q${qNum} ${yearNum}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.revenue += dp.incoming;
      existing.expenses += dp.outgoing;
      existing.balance = dp.balance;
    } else {
      buckets.set(key, {
        year: yearNum,
        revenue: dp.incoming,
        expenses: dp.outgoing,
        balance: dp.balance,
      });
    }
  }

  return Array.from(buckets.entries()).map(([quarter, data]) => ({
    quarter,
    year: data.year,
    balance: data.balance,
    revenue: data.revenue,
    expenses: data.expenses,
    netCashFlow: data.revenue - data.expenses,
  }));
}

function aggregateQuartersToAnnual(
  quarters: { quarter: string; year: number; balance: number; revenue: number; expenses: number; netCashFlow: number }[],
): { year: string; balance: number; revenue: number; expenses: number; netCashFlow: number; growth: number }[] {
  const yearMap = new Map<
    number,
    { balance: number; revenue: number; expenses: number }
  >();

  for (const q of quarters) {
    const existing = yearMap.get(q.year);
    if (existing) {
      existing.revenue += q.revenue;
      existing.expenses += q.expenses;
      existing.balance = q.balance;
    } else {
      yearMap.set(q.year, {
        revenue: q.revenue,
        expenses: q.expenses,
        balance: q.balance,
      });
    }
  }

  const years = Array.from(yearMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([yearNum, data], idx, arr) => ({
      year: String(yearNum),
      balance: data.balance,
      revenue: data.revenue,
      expenses: data.expenses,
      netCashFlow: data.revenue - data.expenses,
      growth: idx === 0 ? 0 : ((data.balance - arr[idx - 1][1].balance) / arr[idx - 1][1].balance * 100),
    }));

  return years;
}

const TEAL = 'oklch(0.5 0.0896 194.8)';
const CONTENT_TXT = '#111827';

type AnnualRowData = { year: string | number; revenue: number; expenses: number; netCashFlow: number; balance: number; growth: number };
const AnnualRow: React.FC<{ row: AnnualRowData; idx: number; formatCurrency: ReturnType<typeof useFormatting>['formatCurrency']; onClick: () => void }> = ({ row, idx, formatCurrency, onClick }) => {
  return (
    <TableRow className={cn("cursor-pointer hover:bg-muted/50 border-0", idx % 2 === 0 && "bg-neutral-50")} onClick={onClick}>
      <TableCell className="font-medium text-primary hover:underline">{row.year}</TableCell>
      <TableCell className="text-right tabular-nums text-success-600">{formatCurrency(row.revenue, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums text-destructive">{formatCurrency(row.expenses, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.netCashFlow, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.balance, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums">
        {row.growth !== 0 && `${row.growth > 0 ? '+' : ''}${row.growth.toFixed(1)}%`}
      </TableCell>
    </TableRow>
  );
};

type QuarterlyRowData = { quarter: string; revenue: number; expenses: number; netCashFlow: number; balance: number };
const QuarterlyRow: React.FC<{ row: QuarterlyRowData; idx: number; formatCurrency: ReturnType<typeof useFormatting>['formatCurrency']; onClick: () => void }> = ({ row, idx, formatCurrency, onClick }) => {
  return (
    <TableRow className={cn("cursor-pointer hover:bg-muted/50 border-0", idx % 2 === 0 && "bg-neutral-50")} onClick={onClick}>
      <TableCell className="font-medium text-primary hover:underline">{row.quarter}</TableCell>
      <TableCell className="text-right tabular-nums text-success-600">{formatCurrency(row.revenue, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums text-destructive">{formatCurrency(row.expenses, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.netCashFlow, 'full')}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(row.balance, 'full')}</TableCell>
    </TableRow>
  );
};

export const Forecast3Year: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const scenarios = useVisibleScenarios();
  const { baseForecast, forecastNotReady } = useBaseForecast();
  const { mode, handleNavigate } = useApp();
  const { hasPermission } = usePermissions();
  const { getForecastTerm, getCashMovementTerms } = useTerminology();
  const isLive = mode === 'live';
  const { data: forecastResponse, isLoading: isForecastLoading, error: forecastError } = useForecast('3y');
  const [viewType, setViewType] = useState<ViewType>('quarterly');
  const [selectedIds, setSelectedIds] = useState<string[]>([BASE_ID]);
  const [drillDownRow, setDrillDownRow] = useState<DrillDownRow | null>(null);
  const activeBalanceIdxRef = useRef<number | null>(null);
  const activeRevenueIdxRef = useRef<number | null>(null);

  const nonBaseIds = selectedIds.filter(id => id !== BASE_ID);
  const { data: scenarioForecastData } = useScenarioForecasts(nonBaseIds, '3y');

  // Derive contributing sources for the selected drill-down period
  const drillDownSources = useMemo(() => {
    if (!drillDownRow) return [];
    if (isLive && forecastResponse?.contributingSources) {
      return forecastResponse.contributingSources
        .filter(s => s.contributedTransactions > 0 || s.contributedBankBalances > 0 || s.contributedOrders > 0 || s.contributedRecurringTransactions > 0)
        .map(s => s.name)
        .sort();
    }
    const [start, end] = drillDownRow.kind === 'quarterly'
      ? getQuarterWindow(drillDownRow.quarter)
      : getAnnualWindow(drillDownRow.year);
    return getContributingSourcesForWindow(start, end);
  }, [drillDownRow, isLive, forecastResponse]);

  const toggleScenario = (id: string) => {
    if (id === BASE_ID) {
      if (selectedIds.length === 1 && selectedIds[0] === BASE_ID) return;
      setSelectedIds(prev =>
        prev.includes(BASE_ID) ? prev.filter(s => s !== BASE_ID) : [BASE_ID, ...prev.filter(s => s !== BASE_ID)],
      );
    } else {
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id],
      );
    }
  };

  const colourFor = (id: string, selectionIndex: number) =>
    SCENARIO_COLOURS[selectionIndex % SCENARIO_COLOURS.length];

  // Label for chart tooltips/legends — includes status
  const labelFor = (id: string) => {
    if (id === BASE_ID) return `Base ${getForecastTerm(true)}`;
    const sc = scenarios.find(s => s.id === id);
    if (!sc) return id;
    return scenarioLabel(sc);
  };

  // Base quarterly and annual data (for metrics + table)
  const baseQuarterlyData = isLive && forecastResponse
    ? aggregateTimelineToQuarters(forecastResponse.timeline)
    : generate3YearData('base');
  const baseAnnualData = isLive && forecastResponse
    ? aggregateQuartersToAnnual(aggregateTimelineToQuarters(forecastResponse.timeline))
    : generateAnnualData('base');

  // Build merged chart data for balance overlay
  const buildBalanceChartData = () => {
    const rows = viewType === 'quarterly' ? baseQuarterlyData : baseAnnualData;
    const dataKey = viewType === 'quarterly' ? 'quarter' : 'year';

    // Pre-generate balance arrays and financials per selected non-base scenario
    const scenarioBalances: Record<string, number[]> = {};
    const scenarioFinancials: Record<string, { revenue: number; expenses: number; netCashFlow: number }[]> = {};
    const base3YearValues = rows.map(r => r.balance);
    for (const id of selectedIds) {
      if (id !== BASE_ID) {
        if (isLive) {
          const forecast = scenarioForecastData[id];
          if (forecast) {
            if (viewType === 'quarterly') {
              const fullData = aggregateScenarioTimelineToQuartersFull(forecast.projectedForecast);
              scenarioBalances[id] = fullData.map(d => d.balance);
              scenarioFinancials[id] = fullData;
            } else {
              const fullData = aggregateScenarioTimelineToAnnualFull(forecast.projectedForecast);
              scenarioBalances[id] = fullData.map(d => d.balance);
              scenarioFinancials[id] = fullData;
            }
          }
        } else {
          const scenario = scenarios.find(s => s.id === id);
          if (scenario && baseForecast.length > 0) {
            if (scenario.changes.length === 0) {
              scenarioBalances[id] = base3YearValues;
              scenarioFinancials[id] = rows.map(d => ({
                revenue: d.revenue,
                expenses: d.expenses,
                netCashFlow: d.netCashFlow,
              }));
            } else {
              const result = applyScenarioToForecast(baseForecast, scenario.changes);
              const netQuarterlyDelta = result.metrics.netImpact / 12;
              scenarioBalances[id] = base3YearValues.map((baseVal, i) =>
                Math.round(baseVal + netQuarterlyDelta * (i + 1)),
              );
              const totalBaseRevenue = rows.reduce((s, r) => s + r.revenue, 0);
              const totalBaseExpenses = rows.reduce((s, r) => s + r.expenses, 0);
              const revenueScale = totalBaseRevenue > 0
                ? (totalBaseRevenue + result.metrics.revenueChangeAmount) / totalBaseRevenue
                : 1;
              const expenseScale = totalBaseExpenses > 0
                ? (totalBaseExpenses + result.metrics.costChangeAmount) / totalBaseExpenses
                : 1;
              scenarioFinancials[id] = rows.map(d => {
                const rev = Math.round(d.revenue * revenueScale);
                const exp = Math.round(d.expenses * expenseScale);
                return { revenue: rev, expenses: exp, netCashFlow: rev - exp };
              });
            }
          }
        }
      }
    }

    return rows.map((row, idx) => {
      const merged: Record<string, unknown> = { [dataKey]: (row as any)[dataKey] };
      // Embed full row data so dot click handlers can build DrillDownRow directly
      merged._kind = viewType === 'quarterly' ? 'quarterly' : 'annual';
      merged._quarter = viewType === 'quarterly' ? (row as any).quarter : undefined;
      merged._year = viewType !== 'quarterly' ? (row as any).year : undefined;
      merged._revenue = row.revenue;
      merged._expenses = row.expenses;
      merged._netCashFlow = row.netCashFlow;
      merged._balance = row.balance;
      merged._growth = (row as any).growth ?? 0;
      if (selectedIds.includes(BASE_ID)) {
        merged[BASE_ID] = row.balance;
      }
      for (const [id, balances] of Object.entries(scenarioBalances)) {
        merged[id] = balances[idx];
        const financials = scenarioFinancials[id]?.[idx];
        if (financials) {
          merged[`_revenue_${id}`] = financials.revenue;
          merged[`_expenses_${id}`] = financials.expenses;
          merged[`_netCashFlow_${id}`] = financials.netCashFlow;
        }
      }
      return merged;
    });
  };

  // Revenue vs Expenses — always base data, no overlay needed here
  const revenueExpenseData = (viewType === 'quarterly' ? baseQuarterlyData : baseAnnualData).map(row => ({
    label: viewType === 'quarterly' ? (row as any).quarter : String((row as any).year),
    revenue: row.revenue,
    expenses: row.expenses,
    netCashFlow: row.netCashFlow,
    // Embed full row data so dot click handlers can build DrillDownRow directly
    _kind: viewType === 'quarterly' ? 'quarterly' : 'annual',
    _quarter: viewType === 'quarterly' ? (row as any).quarter : undefined,
    _year: viewType !== 'quarterly' ? (row as any).year : undefined,
    _revenue: row.revenue,
    _expenses: row.expenses,
    _netCashFlow: row.netCashFlow,
    _balance: row.balance,
    _growth: (row as any).growth ?? 0,
  }));

  const balanceChartData = buildBalanceChartData();

  // Strategic metrics from base data
  const finalBalance = baseAnnualData[baseAnnualData.length - 1].balance;
  const initialBalance = baseQuarterlyData[0].balance;
  const totalGrowth = ((finalBalance - initialBalance) / initialBalance * 100).toFixed(1);
  const avgAnnualGrowth = (parseFloat(totalGrowth) / 3).toFixed(1);
  const totalRevenue = baseAnnualData.reduce((sum, d) => sum + d.revenue, 0);
  const totalExpenses = baseAnnualData.reduce((sum, d) => sum + d.expenses, 0);
  const totalNetCashFlow = totalRevenue - totalExpenses;

  const xKey = viewType === 'quarterly' ? 'quarter' : 'year';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openDrillDown = (payload: any, scenarioId: string = BASE_ID) => {
    const isScenario = scenarioId !== BASE_ID;
    const scenarioName = isScenario
      ? scenarios.find(s => s.id === scenarioId)?.name
      : undefined;

    // Use embedded _kind fields if available (from chart payloads)
    if (payload._kind === 'quarterly' && payload._quarter) {
      const balance = isScenario
        ? (payload[scenarioId] as number) ?? payload._balance ?? 0
        : payload._balance ?? 0;
      const revenue = isScenario
        ? (payload[`_revenue_${scenarioId}`] as number) ?? payload._revenue ?? 0
        : payload._revenue ?? 0;
      const expenses = isScenario
        ? (payload[`_expenses_${scenarioId}`] as number) ?? payload._expenses ?? 0
        : payload._expenses ?? 0;
      const netCashFlow = isScenario
        ? (payload[`_netCashFlow_${scenarioId}`] as number) ?? payload._netCashFlow ?? 0
        : payload._netCashFlow ?? 0;
      setDrillDownRow({
        kind: 'quarterly',
        quarter: payload._quarter,
        year: 0,
        revenue,
        expenses,
        netCashFlow,
        balance,
        scenarioName,
      });
      return;
    }
    if (payload._kind === 'annual' && payload._year) {
      const balance = isScenario
        ? (payload[scenarioId] as number) ?? payload._balance ?? 0
        : payload._balance ?? 0;
      const revenue = isScenario
        ? (payload[`_revenue_${scenarioId}`] as number) ?? payload._revenue ?? 0
        : payload._revenue ?? 0;
      const expenses = isScenario
        ? (payload[`_expenses_${scenarioId}`] as number) ?? payload._expenses ?? 0
        : payload._expenses ?? 0;
      const netCashFlow = isScenario
        ? (payload[`_netCashFlow_${scenarioId}`] as number) ?? payload._netCashFlow ?? 0
        : payload._netCashFlow ?? 0;
      setDrillDownRow({
        kind: 'annual',
        year: payload._year,
        revenue,
        expenses,
        netCashFlow,
        balance,
        growth: payload._growth ?? 0,
        scenarioName,
      });
      return;
    }
    // Fallback: lookup from base data or scenario-specific data
    const label = payload[xKey] ?? payload.label;
    if (!label) return;
    if (viewType === 'quarterly') {
      if (isScenario) {
        const balanceOverride = (payload[scenarioId] as number) ?? undefined;
        if (isLive) {
          const forecast = scenarioForecastData[scenarioId];
          if (forecast) {
            const fullData = aggregateScenarioTimelineToQuartersFull(forecast.projectedForecast);
            const baseRows = baseQuarterlyData;
            const idx = baseRows.findIndex(r => r.quarter === label);
            if (idx >= 0 && idx < fullData.length) {
              const d = fullData[idx];
              setDrillDownRow({ kind: 'quarterly', quarter: label, year: 0, revenue: d.revenue, expenses: d.expenses, netCashFlow: d.netCashFlow, balance: balanceOverride ?? d.balance, scenarioName });
              return;
            }
          }
        } else {
          const scenario = scenarios.find(s => s.id === scenarioId);
          const baseRow = baseQuarterlyData.find(r => r.quarter === label);
          if (baseRow && scenario) {
            if (scenario.changes.length === 0) {
              setDrillDownRow({ kind: 'quarterly', ...baseRow, balance: balanceOverride ?? baseRow.balance, scenarioName });
            } else {
              const result = applyScenarioToForecast(baseForecast, scenario.changes);
              const totalBaseRev = baseQuarterlyData.reduce((s, r) => s + r.revenue, 0);
              const totalBaseExp = baseQuarterlyData.reduce((s, r) => s + r.expenses, 0);
              const revScale = totalBaseRev > 0 ? (totalBaseRev + result.metrics.revenueChangeAmount) / totalBaseRev : 1;
              const expScale = totalBaseExp > 0 ? (totalBaseExp + result.metrics.costChangeAmount) / totalBaseExp : 1;
              const rev = Math.round(baseRow.revenue * revScale);
              const exp = Math.round(baseRow.expenses * expScale);
              setDrillDownRow({ kind: 'quarterly', quarter: label, year: 0, revenue: rev, expenses: exp, netCashFlow: rev - exp, balance: balanceOverride ?? baseRow.balance, scenarioName });
            }
            return;
          }
        }
      }
      const row = baseQuarterlyData.find(r => r.quarter === label);
      if (row) setDrillDownRow({ kind: 'quarterly', ...row, scenarioName });
    } else {
      if (isScenario) {
        const balanceOverride = (payload[scenarioId] as number) ?? undefined;
        if (isLive) {
          const forecast = scenarioForecastData[scenarioId];
          if (forecast) {
            const fullData = aggregateScenarioTimelineToAnnualFull(forecast.projectedForecast);
            const idx = baseAnnualData.findIndex(r => String(r.year) === String(label));
            if (idx >= 0 && idx < fullData.length) {
              const d = fullData[idx];
              setDrillDownRow({ kind: 'annual', year: String(label), revenue: d.revenue, expenses: d.expenses, netCashFlow: d.netCashFlow, balance: balanceOverride ?? d.balance, growth: 0, scenarioName });
              return;
            }
          }
        } else {
          const scenario = scenarios.find(s => s.id === scenarioId);
          const baseRow = baseAnnualData.find(r => String(r.year) === String(label));
          if (baseRow && scenario) {
            if (scenario.changes.length === 0) {
              setDrillDownRow({ kind: 'annual', ...baseRow, year: String(baseRow.year), balance: balanceOverride ?? baseRow.balance, scenarioName });
            } else {
              const result = applyScenarioToForecast(baseForecast, scenario.changes);
              const totalBaseRev = baseAnnualData.reduce((s, r) => s + r.revenue, 0);
              const totalBaseExp = baseAnnualData.reduce((s, r) => s + r.expenses, 0);
              const revScale = totalBaseRev > 0 ? (totalBaseRev + result.metrics.revenueChangeAmount) / totalBaseRev : 1;
              const expScale = totalBaseExp > 0 ? (totalBaseExp + result.metrics.costChangeAmount) / totalBaseExp : 1;
              const rev = Math.round(baseRow.revenue * revScale);
              const exp = Math.round(baseRow.expenses * expScale);
              setDrillDownRow({ kind: 'annual', year: String(baseRow.year), revenue: rev, expenses: exp, netCashFlow: rev - exp, balance: balanceOverride ?? baseRow.balance, growth: 0, scenarioName });
            }
            return;
          }
        }
      }
      const row = baseAnnualData.find(r => String(r.year) === String(label));
      if (row) setDrillDownRow({ kind: 'annual', ...row, scenarioName });
    }
  };

  // ── Cluster picker state ─────────────────────────────────────────────────
  const [clusterState, setClusterState] = useState<{
    candidates: ClusterCandidate[];
    periodLabel: string;
    anchorX: number;
    anchorY: number;
    point: Record<string, unknown>;
  } | null>(null);
  const clusterStateRef = useRef(clusterState);
  // Chart container the cluster picker is anchored to, so it scrolls with the page.
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const setCluster = useCallback((v: typeof clusterState) => {
    clusterStateRef.current = v;
    setClusterState(v);
  }, []);

  // ── Overlay pills scroll state ───────────────────────────────────────────
  const pillsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const nonBasePills = selectedIds.filter(id => id !== BASE_ID);

  const checkScroll = useCallback(() => {
    const el = pillsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
  }, [nonBasePills.length, checkScroll]);

  const scrollPills = (dir: 'left' | 'right') => {
    const el = pillsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -120 : 120, behavior: 'smooth' });
    setTimeout(checkScroll, 150);
  };

  // Recharts v3: track the hovered index via onMouseMove and read on click.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBalanceChartMouseMove = (state: any) => {
    const raw = state?.activeTooltipIndex ?? state?.activeIndex;
    activeBalanceIdxRef.current = raw != null ? Number(raw) : null;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBalanceChartClick = (_state: any, event?: React.MouseEvent) => {
    const idx = activeBalanceIdxRef.current;
    if (idx == null || Number.isNaN(idx) || !balanceChartData[idx]) return;
    const point = balanceChartData[idx];

    // Build candidate list from every selected series that has a value at this point
    const candidates: ClusterCandidate[] = selectedIds
      .filter(id => typeof point[id] === 'number')
      .map((id) => ({
        id,
        label: labelFor(id),
        value: point[id] as number,
        colour: colourFor(id, selectedIds.indexOf(id)),
      }));

    if (candidates.length === 0) return;

    // Single series → fire directly, no picker needed
    if (candidates.length === 1) {
      openDrillDown(point, candidates[0].id);
      return;
    }

    // Multiple series → show cluster picker at click coordinates
    if (event) {
      const periodLabel = viewType === 'quarterly'
        ? (point.quarter as string)
        : String(point.year);
      setCluster({
        candidates,
        periodLabel,
        anchorX: event.clientX,
        anchorY: event.clientY,
        point,
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRevenueChartMouseMove = (state: any) => {
    const raw = state?.activeTooltipIndex ?? state?.activeIndex;
    activeRevenueIdxRef.current = raw != null ? Number(raw) : null;
  };
  const handleRevenueChartClick = () => {
    const idx = activeRevenueIdxRef.current;
    if (idx != null && !Number.isNaN(idx) && revenueExpenseData[idx]) {
      openDrillDown(revenueExpenseData[idx], BASE_ID);
    }
  };

  if (!hasPermission('view_3year_forecast_screen')) return null;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
          <p className="font-semibold mb-1.5">{label}</p>
          {payload.filter((e: any) => e.type !== 'none').map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
              <span className="text-muted-foreground capitalize">{entry.name}:</span>
              <span className="font-semibold ml-auto pl-3">{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLive && isForecastLoading) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading 3-year {getForecastTerm(false)} data...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLive && (forecastNotReady || (forecastError instanceof ApiError && forecastError.status === 404))) {
    return (
      <div className="p-6">
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No {getForecastTerm(false)} data available
            </h3>
            <p className="text-muted-foreground text-sm max-w-md mb-6">
              A {getForecastTerm(false)} refresh has not been run yet. Run a{" "}
              {getForecastTerm(false)} refresh to populate this screen with cash
              position data and projections.
            </p>
            {hasPermission("trigger_forecast_refresh") && (
              <Button onClick={() => handleNavigate("forecast-refresh")}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Go to Refresh {getForecastTerm(true)}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLive && forecastError) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive py-12">
              {forecastError instanceof Error ? forecastError.message : 'Failed to load 3-year forecast'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">

      {/* Strategic Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 3-Year Growth */}
        <Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">3-Year Growth</span>
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--mc-info-bg)' }}>
                <TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">+{totalGrowth}%</div>
              <div className="text-sm text-neutral-800 mt-1">From {formatCurrency(initialBalance)} to {formatCurrency(finalBalance)}</div>
            </div>
          </CardContent>
        </Card>

        {/* Avg Annual Growth */}
        <Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">Avg Annual Growth</span>
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--mc-info-bg)' }}>
                <Calendar className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">+{avgAnnualGrowth}%</div>
              <div className="text-sm text-neutral-800 mt-1">Year-over-year average</div>
            </div>
          </CardContent>
        </Card>

        {/* Total Net Cash Flow */}
        <Card className="h-full" style={{ borderLeft: `3px solid var(${totalNetCashFlow >= 0 ? '--mc-info-border' : '--mc-error-border'})` }}>
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">Total Net Cash Flow</span>
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `var(${totalNetCashFlow >= 0 ? '--mc-info-bg' : '--mc-error-bg'})` }}>
                {totalNetCashFlow >= 0
                  ? <TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
                  : <TrendingDown className="h-4 w-4" style={{ color: 'var(--mc-error-icon)' }} />}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(totalNetCashFlow)}
              </div>
              <div className="text-sm text-neutral-800 mt-1">3-year cumulative</div>
            </div>
          </CardContent>
        </Card>

        {/* Final Position */}
        <Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">Final Position</span>
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--mc-info-bg)' }}>
                <Target className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(finalBalance)}</div>
              <div className="text-sm text-neutral-800 mt-1">End of Year 3</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Balance Trajectory Chart — multi-scenario overlay */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4 px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-neutral-900">Cash Balance Trajectory</span>
            <InfoTooltip title="Cash Balance Trajectory" content="3-year cash balance projection. Click any point to see the breakdown for that period." />
          </div>
          <div className="flex items-center gap-3">
            <Tabs value={viewType} onValueChange={(v) => setViewType(v as ViewType)}>
              <TabsList>
                <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
                <TabsTrigger value="annual">Annual</TabsTrigger>
              </TabsList>
            </Tabs>
            <ScenarioOverlayPicker
              scenarios={scenarios}
              selectedIds={selectedIds}
              onToggle={toggleScenario}
            />
            {selectedIds.filter(id => id !== BASE_ID).length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([BASE_ID])}
                className="text-sm text-muted-foreground hover:text-destructive transition-colors whitespace-nowrap"
              >
                Deselect all
              </button>
            )}
          </div>
        </div>
        {nonBasePills.length > 0 && (
          <div className="relative flex items-center px-4 py-2 border-b">
            {canScrollLeft && (
              <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 mr-1" onClick={() => scrollPills('left')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <div
              ref={pillsScrollRef}
              className="flex items-center gap-2 overflow-x-auto scrollbar-none flex-1"
              onScroll={checkScroll}
            >
              {nonBasePills.map((id, idx) => {
                const sc = scenarios.find(s => s.id === id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border flex-shrink-0"
                    style={{ backgroundColor: `${SCENARIO_COLOURS[idx % SCENARIO_COLOURS.length]}1a`, borderColor: `${SCENARIO_COLOURS[idx % SCENARIO_COLOURS.length]}40`, color: SCENARIO_COLOURS[idx % SCENARIO_COLOURS.length] }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SCENARIO_COLOURS[idx % SCENARIO_COLOURS.length] }} />
                    {sc?.name ?? id}
                    <button
                      className="ml-0.5 hover:opacity-70"
                      onClick={() => toggleScenario(id)}
                      aria-label={`Remove ${sc?.name ?? id}`}
                    >×</button>
                  </span>
                );
              })}
            </div>
            {canScrollRight && (
              <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 ml-1" onClick={() => scrollPills('right')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        <CardContent>
          {/* Legend — above chart, matches 90-day/12-month style */}
          <div className="mb-3 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: CHART_LINE_COLOR }} />
              <span>Base {getForecastTerm(true)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-0 border-t-2 border-dashed" style={{ borderColor: CHART_THRESHOLD_COLOR }} />
              <span>Threshold ({getCurrencySymbol()}50K)</span>
            </div>
          </div>

          <div ref={chartContainerRef} className="relative h-[400px] w-full" style={{ cursor: 'pointer' }}>
            {clusterState && (
              <ClusterPickerPopover
                candidates={clusterState.candidates}
                periodLabel={clusterState.periodLabel}
                anchorX={clusterState.anchorX}
                anchorY={clusterState.anchorY}
                containerRef={chartContainerRef}
                onSelect={(id) => {
                  openDrillDown(clusterState.point, id);
                  setCluster(null);
                }}
                onDismiss={() => setCluster(null)}
              />
            )}
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={balanceChartData} onClick={handleBalanceChartClick} onMouseMove={handleBalanceChartMouseMove}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey={xKey}
                  tick={{ fontSize: 12 }}
                  angle={viewType === 'quarterly' ? -45 : 0}
                  textAnchor={viewType === 'quarterly' ? 'end' : 'middle'}
                  height={viewType === 'quarterly' ? 80 : 30}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip content={clusterState ? () => null : <CustomTooltip />} />

                {/* Balance lines per selected scenario */}
                {selectedIds.map((id, idx) => (
                  <Line
                    key={`line-${id}`}
                    type="monotone"
                    dataKey={id}
                    name={labelFor(id)}
                    stroke={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
                    strokeWidth={id === BASE_ID ? 3 : 2}
                    connectNulls={false}
                    dot={false}
                    activeDot={{ r: 5, fill: id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx), cursor: 'pointer' }}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

        </CardContent>
      </Card>

      {/* Revenue vs Expenses Chart */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-neutral-900">Revenue vs Expenses Trend</span>
            <InfoTooltip title="Revenue vs Expenses" content="Quarterly incoming vs outgoing cash. Click any point to drill into the period breakdown." />
          </div>
        </div>
        <CardContent>
          {/* Legend — above chart, matches 90-day/12-month style */}
          <div className="mb-3 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: 'var(--mc-success-icon)' }} />
              <span>{getCashMovementTerms().in}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: CHART_THRESHOLD_COLOR }} />
              <span>{getCashMovementTerms().out}</span>
            </div>
          </div>
          <div className="h-[350px] w-full" style={{ cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueExpenseData} onClick={handleRevenueChartClick} onMouseMove={handleRevenueChartMouseMove}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  angle={viewType === 'quarterly' ? -45 : 0}
                  textAnchor={viewType === 'quarterly' ? 'end' : 'middle'}
                  height={viewType === 'quarterly' ? 80 : 30}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip content={clusterState ? () => null : <CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--mc-success-icon)"
                  strokeWidth={3}
                  name={getCashMovementTerms().in}
                  dot={false}
                  activeDot={{ r: 5, fill: 'var(--mc-success-icon)', cursor: 'pointer' }}
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke={CHART_THRESHOLD_COLOR}
                  strokeWidth={3}
                  name={getCashMovementTerms().out}
                  dot={false}
                  activeDot={{ r: 5, fill: CHART_THRESHOLD_COLOR, cursor: 'pointer' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

        </CardContent>
      </Card>

      {/* Annual Summary Table */}
      {viewType === 'annual' && (
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-neutral-900">Annual Summary</span>
              <InfoTooltip title="Annual Summary" content="Year-by-year cash flow summary. Click a row to drill into the period." />
            </div>
          </div>
          <CardContent className="pt-4">
            <Table>
              <TableHeader className="sticky top-0 z-40 bg-white [&_tr]:border-0 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-border after:z-50">
                <TableRow className="hover:bg-transparent border-0">
                  <TableHead className="font-semibold text-foreground bg-white">Year</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">{getCashMovementTerms().in}</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">{getCashMovementTerms().out}</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">Net Cash Flow</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">Closing Balance</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">Growth</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr]:border-b-0">
                {baseAnnualData.map((row, idx) => (
                  <AnnualRow
                    key={row.year}
                    row={row}
                    idx={idx}
                    formatCurrency={formatCurrency}
                    onClick={() => setDrillDownRow({ kind: 'annual', ...row })}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Quarterly Breakdown Table */}
      {viewType === 'quarterly' && (
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-neutral-900">Quarterly Breakdown</span>
              <InfoTooltip title="Quarterly Breakdown" content="Quarter-by-quarter cash flow breakdown. Click a row to drill into the period." />
            </div>
          </div>
          <CardContent className="pt-4">
            <Table>
              <TableHeader className="sticky top-0 z-40 bg-white [&_tr]:border-0 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-border after:z-50">
                <TableRow className="hover:bg-transparent border-0">
                  <TableHead className="font-semibold text-foreground bg-white">Quarter</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">{getCashMovementTerms().in}</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">{getCashMovementTerms().out}</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">Net Cash Flow</TableHead>
                  <TableHead className="text-right font-semibold text-foreground bg-white">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr]:border-b-0">
                {baseQuarterlyData.map((row, idx) => (
                  <React.Fragment key={row.quarter}>
                    {idx % 4 === 0 && idx !== 0 && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={5} className="py-1" />
                      </TableRow>
                    )}
                    <QuarterlyRow
                      row={row}
                      idx={idx}
                      formatCurrency={formatCurrency}
                      onClick={() => setDrillDownRow({ kind: 'quarterly', ...row })}
                    />
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Strategic Insights */}
      <Card>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-neutral-900">Strategic Insights & Recommendations</span>
            <InfoTooltip title="Strategic Insights" content="AI-generated strategic insights based on your 3-year forecast trajectory." />
          </div>
        </div>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-warning-bg)', borderColor: 'var(--mc-warning-border)' }}>
              <Info className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-warning-icon)' }} />
              <div>
                <div className="font-semibold" style={{ color: 'var(--mc-warning-icon)' }}>Near-Term Cash Flow Challenge</div>
                <div className="text-sm" style={{ color: 'var(--mc-warning-icon)' }}>
                  The {getForecastTerm(false)} shows a significant cash decline in Q1 (down to {formatCurrency(FORECAST_90_DAY_ENDPOINT)}),
                  creating urgency for immediate action. However, with proper cash management and the AI recommendations
                  implemented, recovery begins in Q2 and the business achieves strong growth over 3 years (+{totalGrowth}%).
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-info-bg)', borderColor: 'var(--mc-info-border)' }}>
              <TrendingUp className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-info-icon)' }} />
              <div>
                <div className="font-semibold" style={{ color: 'var(--mc-info-icon)' }}>Recovery & Growth Strategy</div>
                <div className="text-sm" style={{ color: 'var(--mc-info-icon)' }}>
                  After navigating the Q1 crisis, focus on: (1) Collecting overdue invoices, (2) Negotiating better payment terms,
                  (3) Accelerating project billing. By Year 2–3, consider strategic investments in equipment upgrades,
                  market expansion, or hiring key talent to accelerate growth.
                </div>
              </div>
            </div>

            {/* Per-scenario insight callouts */}
            {selectedIds.filter(id => id !== BASE_ID).map((id) => {
              const sc = scenarios.find(s => s.id === id);
              if (!sc) return null;
              const colour = colourFor(id, selectedIds.indexOf(id));
              return (
                <div
                  key={id}
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{ borderColor: `${colour}40`, backgroundColor: `${colour}0d` }}
                >
                  <Info className="h-5 w-5 mt-0.5" style={{ color: colour }} />
                  <div>
                    <div className="font-semibold" style={{ color: colour }}>
                      Scenario: {sc.name}
                    </div>
                    <div className="text-sm text-muted-foreground">{sc.description}</div>
                  </div>
                </div>
              );
            })}

            <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--mc-neutral-bg)', borderColor: 'var(--mc-neutral-border)' }}>
              <Info className="h-5 w-5 mt-0.5" style={{ color: 'var(--mc-neutral-icon)' }} />
              <div>
                <div className="font-semibold" style={{ color: 'var(--accent-neutral-700)' }}>Planning Recommendations</div>
                <div className="text-sm" style={{ color: 'var(--mc-neutral-icon)' }}>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>Review and update this {getForecastTerm(false)} quarterly based on actual performance</li>
                    <li>Maintain a cash reserve of at least 3–6 months operating expenses</li>
                    <li>Consider securing a line of credit for unexpected opportunities or challenges</li>
                    <li>Plan major capital expenditures during periods of strong cash flow</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Period Drill-Down Dialog */}
      <Dialog open={drillDownRow !== null} onOpenChange={(open) => { if (!open) setDrillDownRow(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">
              {drillDownRow?.kind === 'quarterly' ? drillDownRow.quarter : drillDownRow?.year} — Breakdown{drillDownRow?.scenarioName ? ` — ${drillDownRow.scenarioName}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div style={{ borderBottom: '1px solid #e5e7eb', margin: '0 -24px' }} />
          {drillDownRow && (
            <div className="space-y-4 pt-2">
              {/* Summary metric cards */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-success-border)' }}>
                  <div className="text-xs text-foreground mb-1">{getCashMovementTerms().in}</div>
                  <div className="font-semibold" style={{ color: 'var(--mc-success-icon)' }}>{formatCurrency(drillDownRow.revenue, 'full')}</div>
                </div>
                <div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-error-border)' }}>
                  <div className="text-xs text-foreground mb-1">{getCashMovementTerms().out}</div>
                  <div className="font-semibold" style={{ color: 'var(--mc-error-icon)' }}>{formatCurrency(drillDownRow.expenses, 'full')}</div>
                </div>
                <div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
                  <div className="text-xs text-foreground mb-1">Net Cash Flow</div>
                  <div className="font-semibold text-foreground bg-white">{drillDownRow.netCashFlow >= 0 ? '+' : ''}{formatCurrency(drillDownRow.netCashFlow, 'full')}</div>
                </div>
                <div className="rounded-lg border bg-card p-3" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
                  <div className="text-xs text-foreground mb-1">Closing Balance</div>
                  <div className="font-semibold text-foreground bg-white">{formatCurrency(drillDownRow.balance, 'full')}</div>
                </div>
              </div>
              {drillDownRow.kind === 'annual' && drillDownRow.growth !== 0 && (
                <div className="rounded-lg border bg-card p-3 text-sm" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
                  <div className="text-xs text-foreground mb-1">Year-on-Year Growth</div>
                  <div className="font-semibold text-foreground bg-white">{drillDownRow.growth > 0 ? '+' : ''}{drillDownRow.growth.toFixed(1)}%</div>
                </div>
              )}
              {/* Contributing Sources */}
              {drillDownSources.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-foreground mb-2">Contributing Data Sources</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {drillDownSources.map((source) => {
                      const isManual = source.startsWith('Manual Entry');
                      return (
                        <span
                          key={source}
                          style={isManual
                          ? { backgroundColor: 'var(--mc-warning-bg)', color: 'var(--mc-warning-icon)', border: '1px solid var(--mc-warning-border)' }
                          : { backgroundColor: 'var(--mc-neutral-bg)', color: 'var(--mc-neutral-icon)', border: '1px solid var(--mc-neutral-border)' }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                        >
                          {isManual
                            ? <PenLine className="h-3 w-3" />
                            : <Database className="h-3 w-3" />
                          }
                          {source}
                        </span>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Figures aggregated across the above source{drillDownSources.length > 1 ? 's' : ''}. Drill into individual transactions to verify each record.
                  </p>
                </div>
              )}

              {/* Margin note */}
              <p className="text-xs text-muted-foreground">
                Based on {drillDownRow?.scenarioName ?? 'Base'} {getForecastTerm(true)}. Figures are projected — actual performance may vary.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
